import { Users, Target, Heart, Award, Globe, Lightbulb } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              ¿Quiénes Somos?
            </h1>
            <p className="text-xl md:text-2xl opacity-90">
              Transformamos la educación en participación cívica activa
            </p>
          </div>
        </div>
      </section>

      {/* Misión y Visión */}
      <section className="py-16 container mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
              <Target className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Nuestra Misión</h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Empoderar a ciudadanos de habla hispana para que se conviertan en agentes de cambio
              mediante educación de calidad y oportunidades concretas de participación cívica.
              Creemos que el conocimiento sin acción es incompleto, y la acción sin conocimiento
              es arriesgada.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
              <Lightbulb className="w-8 h-8 text-purple-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Nuestra Visión</h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Ser la plataforma líder en el mundo hispanohablante que conecta educación con
              activismo cívico, creando una red de ciudadanos informados, comprometidos y activos
              que transforman sus comunidades mediante el liderazgo del cambio positivo.
            </p>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Nuestros Valores
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Empatía</h3>
              <p className="text-gray-600">
                Comprendemos las necesidades de nuestras comunidades y diseñamos soluciones
                centradas en las personas.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Excelencia</h3>
              <p className="text-gray-600">
                Ofrecemos contenido educativo de alta calidad y campañas de impacto real
                verificado.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Inclusión</h3>
              <p className="text-gray-600">
                Creamos espacios seguros y accesibles donde todas las voces son escuchadas y
                valoradas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Equipo */}
      <section className="py-16 container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
          Nuestro Equipo
        </h2>
        <p className="text-center text-gray-600 text-lg mb-12 max-w-3xl mx-auto">
          Un grupo multidisciplinario de educadores, activistas, desarrolladores y diseñadores
          comprometidos con el cambio social.
        </p>

        <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {/* Miembro 1 */}
          <div className="text-center">
            <div className="w-32 h-32 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Users className="w-16 h-16 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-1">María González</h3>
            <p className="text-blue-600 font-medium mb-2">Directora Ejecutiva</p>
            <p className="text-sm text-gray-600">
              15 años en educación digital y diseño de políticas públicas
            </p>
          </div>

          {/* Miembro 2 */}
          <div className="text-center">
            <div className="w-32 h-32 bg-gradient-to-br from-green-400 to-green-600 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Users className="w-16 h-16 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-1">Carlos Martínez</h3>
            <p className="text-green-600 font-medium mb-2">Director de Activismo</p>
            <p className="text-sm text-gray-600">
              Organizador comunitario con 12 años de experiencia en ONGs
            </p>
          </div>

          {/* Miembro 3 */}
          <div className="text-center">
            <div className="w-32 h-32 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Users className="w-16 h-16 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-1">Laura Sánchez</h3>
            <p className="text-purple-600 font-medium mb-2">Directora de Contenido</p>
            <p className="text-sm text-gray-600">
              Pedagoga especializada en aprendizaje transformacional
            </p>
          </div>

          {/* Miembro 4 */}
          <div className="text-center">
            <div className="w-32 h-32 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Users className="w-16 h-16 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-1">Javier López</h3>
            <p className="text-orange-600 font-medium mb-2">Director de Tecnología</p>
            <p className="text-sm text-gray-600">
              Desarrollador full-stack enfocado en impacto social
            </p>
          </div>
        </div>
      </section>

      {/* Historia */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-8">Nuestra Historia</h2>
            <div className="space-y-6 text-lg">
              <p className="leading-relaxed">
                <strong>Learn to Lead Change</strong> nació en 2020 de una simple observación:
                muchas personas querían crear cambio positivo en sus comunidades, pero no sabían
                por dónde empezar. Tenían las ganas, pero les faltaba el conocimiento específico
                o las conexiones para pasar a la acción.
              </p>
              <p className="leading-relaxed">
                Al mismo tiempo, existían miles de campañas de activismo buscando voluntarios,
                pero carecían de personas con las habilidades necesarias para liderarlas
                efectivamente. Vimos la oportunidad de crear un puente entre estos dos mundos.
              </p>
              <p className="leading-relaxed">
                Empezamos con 10 cursos y 5 campañas. Hoy, con más de <strong>12,000 socios</strong>,
                <strong> 85 cursos</strong> y <strong>350+ campañas activas</strong>, hemos demostrado
                que la educación y la acción no solo pueden coexistir, sino potenciarse mutuamente.
              </p>
              <p className="leading-relaxed">
                Nuestros socios han liderado cambios reales: desde políticas de reciclaje municipal
                hasta programas de educación financiera en barrios vulnerables. Cada curso completado
                se convierte en acción, cada acción alimenta más aprendizaje.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center bg-white rounded-2xl shadow-xl p-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            ¿Listo para Liderar el Cambio?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Únete a nuestra comunidad de agentes de cambio y comienza tu viaje de transformación hoy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/registro"
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
            >
              Únete Gratis
            </a>
            <a
              href="/contact"
              className="px-8 py-4 bg-gray-100 text-gray-900 rounded-lg font-semibold hover:bg-gray-200 transition-all"
            >
              Contáctanos
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
